import { POPULATION_STATS, WHO_CHILD_MEDIAN, type Population } from "@/data/bdHeightStats";

export type Gender = "male" | "female";

// ---------------------------------------------------------------------------
// Mid-parental height (Tanner formula)
// ---------------------------------------------------------------------------
export interface MidParentalInput {
  gender: Gender;
  fatherCm: number;
  motherCm: number;
}

export interface MidParentalResult {
  midParentCm: number;
  predictedCm: number;
  lowCm: number;
  highCm: number;
}

export function midParentalHeight(i: MidParentalInput): MidParentalResult {
  const mid = (i.fatherCm + i.motherCm) / 2;
  const adj = i.gender === "male" ? 6.5 : -6.5;
  const predicted = mid + adj;
  return {
    midParentCm: mid,
    predictedCm: predicted,
    lowCm: predicted - 8.5,
    highCm: predicted + 8.5,
  };
}

// ---------------------------------------------------------------------------
// Khamis-Roche style estimate (simplified)
// ---------------------------------------------------------------------------
// True Khamis-Roche uses a large coefficient table by age & sex (Pediatrics 1994).
// We implement a transparent simplified version that combines the child's
// current height-projected adult height with the mid-parental estimate,
// weighted by age. Accuracy is lower than full KR, so we always show a range.
export interface KhamisRocheInput extends MidParentalInput {
  ageYears: number; // child's age, decimal years (e.g. 8.5)
  childHeightCm: number;
  childWeightKg?: number; // accepted for future use; not required here
}

export interface KhamisRocheResult extends MidParentalResult {
  fromCurrentCm: number; // adult height projected from current height alone
  weightUsed: number; // weighting given to current-height projection (0..1)
}

// Approximate fraction of adult height attained at a given age (averaged sexes).
// Source: rounded summary of CDC/WHO percent-of-adult-height tables.
function fractionOfAdultByAge(age: number, gender: Gender): number {
  // Clamp into reference range
  const a = Math.max(2, Math.min(18, age));
  // Boys
  const boys: Array<[number, number]> = [
    [2, 0.49], [3, 0.54], [4, 0.58], [5, 0.62], [6, 0.66], [7, 0.69],
    [8, 0.72], [9, 0.75], [10, 0.78], [11, 0.81], [12, 0.85],
    [13, 0.88], [14, 0.92], [15, 0.96], [16, 0.98], [17, 0.99], [18, 1.0],
  ];
  const girls: Array<[number, number]> = [
    [2, 0.52], [3, 0.57], [4, 0.62], [5, 0.66], [6, 0.70], [7, 0.74],
    [8, 0.77], [9, 0.81], [10, 0.84], [11, 0.88], [12, 0.93],
    [13, 0.96], [14, 0.98], [15, 0.99], [16, 0.995], [17, 1.0], [18, 1.0],
  ];
  const table = gender === "male" ? boys : girls;
  // Linear interpolation
  for (let i = 0; i < table.length - 1; i++) {
    const [x0, y0] = table[i];
    const [x1, y1] = table[i + 1];
    if (a >= x0 && a <= x1) {
      const t = (a - x0) / (x1 - x0);
      return y0 + t * (y1 - y0);
    }
  }
  return 1;
}

export function khamisRocheEstimate(i: KhamisRocheInput): KhamisRocheResult {
  const mp = midParentalHeight(i);
  const frac = fractionOfAdultByAge(i.ageYears, i.gender);
  const fromCurrent = i.childHeightCm / frac;

  // Weight the current-height projection more heavily as the child gets older.
  // Younger children: lean on parent heights. Older: lean on current trajectory.
  const w = Math.max(0, Math.min(0.85, (i.ageYears - 4) / 14)); // 0 at age 4, 0.85 at 18
  const predicted = w * fromCurrent + (1 - w) * mp.predictedCm;

  // Wider range for early ages, tighter for adolescents
  const halfRange = 8.5 - (i.ageYears - 4) * 0.25; // ~8.5 cm at 4y → ~5 cm at 18y
  const half = Math.max(4, halfRange);

  return {
    midParentCm: mp.midParentCm,
    predictedCm: predicted,
    lowCm: predicted - half,
    highCm: predicted + half,
    fromCurrentCm: fromCurrent,
    weightUsed: w,
  };
}

// ---------------------------------------------------------------------------
// Height percentile via normal distribution
// ---------------------------------------------------------------------------
function erf(x: number): number {
  // Abramowitz & Stegun 7.1.26 approximation
  const sign = Math.sign(x);
  const ax = Math.abs(x);
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const t = 1 / (1 + p * ax);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax);
  return sign * y;
}

function normalCdf(z: number): number {
  return 0.5 * (1 + erf(z / Math.SQRT2));
}

export interface PercentileInput {
  gender: Gender;
  heightCm: number;
  population: Population;
  // childAge is optional — when provided we additionally return WHO median context
  childAge?: number;
}

export interface PercentileResult {
  percentile: number;
  zScore: number;
  meanCm: number;
  sdCm: number;
  whoMedianCm?: number;
}

export function heightPercentile(i: PercentileInput): PercentileResult {
  const stats = POPULATION_STATS[i.population];
  const mean = i.gender === "male" ? stats.malesMeanCm : stats.femalesMeanCm;
  const sd = i.gender === "male" ? stats.malesSdCm : stats.femalesSdCm;
  const z = (i.heightCm - mean) / sd;
  const pct = normalCdf(z) * 100;

  let whoMedian: number | undefined;
  if (i.childAge !== undefined) {
    const a = Math.max(2, Math.min(18, Math.round(i.childAge)));
    const row = WHO_CHILD_MEDIAN.find((r) => r.age === a);
    if (row) whoMedian = i.gender === "male" ? row.M : row.F;
  }

  return { percentile: pct, zScore: z, meanCm: mean, sdCm: sd, whoMedianCm: whoMedian };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
export function cmToFeetInches(cm: number): { ft: number; inches: number } {
  const totalIn = cm / 2.54;
  const ft = Math.floor(totalIn / 12);
  const inches = totalIn - ft * 12;
  return { ft, inches };
}

export function feetInchesToCm(ft: number, inches: number): number {
  return (ft * 12 + inches) * 2.54;
}

export function formatFeetInches(cm: number): string {
  const { ft, inches } = cmToFeetInches(cm);
  return `${ft}'${inches.toFixed(1)}\"`;
}
